import React, { useRef, useState } from 'react'
import { mdiCamera, mdiUpload, mdiClose } from '@mdi/js'
import Icon from '@mdi/react'
import Camera, { FACING_MODES } from 'react-html5-camera-photo'
import 'react-html5-camera-photo/build/css/index.css'
import {
  isMobileDevice,
  hasCameraSupport
} from '../../../share/utils/deviceDetection'
import { useDialog } from '../../../hooks/useDialog'
import './styles.scss'

interface DocumentUploadModalProps {
  /** Whether the modal is open */
  isOpen: boolean
  /** Callback when modal should be closed */
  onClose: () => void
  /** Callback when files are selected */
  onFilesSelected: (files: File[]) => void
  /** Whether upload is in progress */
  isUploading?: boolean
}

/**
 * Modal component for uploading documents with support for file selection and camera capture
 */
const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  isOpen,
  onClose,
  onFilesSelected,
  isUploading = false
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [showCamera, setShowCamera] = useState(false)
  const [cameraLoading, setCameraLoading] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)

  // Use custom dialog hook with fallback support
  const dialogRef = useDialog(isOpen, () => {
    setShowCamera(false)
    onClose()
  })

  /**
   * Validates if a file meets upload requirements
   */
  const isValidFile = (file: File): boolean => {
    const validTypes = [
      // Images
      'image/jpeg',
      'image/jpg',
      'image/gif',
      'image/png',
      'image/bmp',
      // Documents
      'application/pdf',
      'application/msword', // .doc
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'text/plain', // .txt
      'application/rtf', // .rtf
      'application/vnd.wordperfect' // .wpd
    ]

    // Also check by file extension as MIME types can be unreliable
    const validExtensions = [
      '.pdf',
      '.jpg',
      '.jpeg',
      '.gif',
      '.png',
      '.bmp',
      '.wpd',
      '.txt',
      '.rtf',
      '.doc',
      '.docx'
    ]

    const fileExtension = file.name
      .toLowerCase()
      .substring(file.name.lastIndexOf('.'))
    const isValidByType = validTypes.includes(file.type)
    const isValidByExtension = validExtensions.includes(fileExtension)

    if (!isValidByType && !isValidByExtension) {
      console.error(
        `File ${file.name} is not a valid file type. Allowed types: PDF, JPG, JPEG, GIF, PNG, BMP, WPD, TXT, RTF, DOC, DOCX`
      )
      return false
    }

    const maxSize = 25 * 1024 * 1024 // 25MB
    if (file.size > maxSize) {
      console.error(`File ${file.name} exceeds the maximum size of 25MB`)
      return false
    }

    return true
  }

  /**
   * Handles file selection from file input
   */
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files
    if (!selectedFiles) return

    const filesArray = Array.from(selectedFiles)
    const validFiles = filesArray.filter(isValidFile)

    if (validFiles.length > 0) {
      onFilesSelected(validFiles)
      onClose()
    }

    // Reset file input
    e.target.value = ''
  }

  /**
   * Handles drag and drop events
   */
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isUploading) {
      setIsDragOver(true)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isUploading) {
      setIsDragOver(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    if (isUploading) return

    const files = Array.from(e.dataTransfer.files)
    const validFiles = files.filter(isValidFile)

    if (validFiles.length > 0) {
      onFilesSelected(validFiles)
      onClose()
    }
  }

  /**
   * Handles photo capture from camera
   */
  const handleTakePhoto = (dataUri: string) => {
    // Convert data URI to File object
    const byteString = atob(dataUri.split(',')[1])
    const mimeString = dataUri.split(',')[0].split(':')[1].split(';')[0]
    const ab = new ArrayBuffer(byteString.length)
    const ia = new Uint8Array(ab)

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i)
    }

    const blob = new Blob([ab], { type: mimeString })
    const file = new File([blob], `photo_${Date.now()}.jpg`, {
      type: 'image/jpeg'
    })

    onFilesSelected([file])
    onClose()
  }

  /**
   * Handles camera errors
   */
  const handleCameraError = (error: Error) => {
    console.error('Camera error:', error)
    setCameraLoading(false)
    setShowCamera(false)
  }

  /**
   * Opens camera view
   */
  const openCamera = () => {
    setCameraLoading(true)
    setShowCamera(true)
  }

  /**
   * Closes camera view
   */
  const closeCamera = () => {
    setShowCamera(false)
    setCameraLoading(false)
  }

  // Check if we should show camera option
  const shouldShowCamera = isMobileDevice() && hasCameraSupport()

  return (
    <dialog ref={dialogRef} className='document-upload-modal'>
      <div className='document-upload-modal__content'>
        <header className='document-upload-modal__header'>
          <h2 className='document-upload-modal__title'>Add Documents</h2>
          <button
            type='button'
            className='document-upload-modal__close'
            onClick={onClose}
            disabled={isUploading}
            aria-label='Close modal'
          >
            <Icon path={mdiClose} size={1} />
          </button>
        </header>

        <div
          className={`document-upload-modal__body ${
            isDragOver ? 'drag-over' : ''
          }`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {showCamera ? (
            <div className='document-upload-modal__camera'>
              <div className='document-upload-modal__camera-header'>
                <h3>Take a Photo</h3>
                <button
                  type='button'
                  className='document-upload-modal__camera-close'
                  onClick={closeCamera}
                  aria-label='Close camera'
                >
                  <Icon path={mdiClose} size={0.8} />
                </button>
              </div>

              {cameraLoading && (
                <div className='document-upload-modal__camera-loading'>
                  <div className='loading-spinner'>
                    <div></div>
                    <div></div>
                    <div></div>
                  </div>
                  <p>Loading camera...</p>
                </div>
              )}

              <div
                className={`document-upload-modal__camera-container ${
                  cameraLoading ? 'hidden' : ''
                }`}
              >
                <Camera
                  onCameraStart={() => setCameraLoading(false)}
                  isImageMirror={false}
                  idealFacingMode={FACING_MODES.ENVIRONMENT}
                  onTakePhoto={handleTakePhoto}
                  onCameraError={handleCameraError}
                />
              </div>
            </div>
          ) : (
            <div className='document-upload-modal__options'>
              {isDragOver ? (
                <div className='document-upload-modal__drag-zone'>
                  <Icon path={mdiUpload} size={3} />
                  <h3>Drop files here</h3>
                  <p>Release to upload your documents</p>
                </div>
              ) : (
                <>
                  <p className='document-upload-modal__description'>
                    Choose how you'd like to add your documents:
                  </p>

                  <div className='document-upload-modal__buttons'>
                    {shouldShowCamera && (
                      <button
                        type='button'
                        className='document-upload-modal__option-button'
                        onClick={openCamera}
                        disabled={isUploading}
                      >
                        <Icon path={mdiCamera} size={1.5} />
                        <span>Take Photo</span>
                      </button>
                    )}

                    <button
                      type='button'
                      className='document-upload-modal__option-button'
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      <Icon path={mdiUpload} size={1.5} />
                      <span>Upload Files</span>
                    </button>
                  </div>

                  <div className='document-upload-modal__info'>
                    <p>
                      <small>
                        Accepted formats: PDF, JPG, JPEG, GIF, PNG, BMP, WPD,
                        TXT, RTF, DOC, DOCX
                        <br />
                        Maximum file size: 25MB per file
                        <br />
                        <strong>
                          Or drag and drop files anywhere in this area
                        </strong>
                      </small>
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type='file'
          multiple
          accept='.pdf,.jpg,.jpeg,.gif,.png,.bmp,.wpd,.txt,.rtf,.doc,.docx,image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/rtf,application/vnd.wordperfect'
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </div>
    </dialog>
  )
}

export default DocumentUploadModal
